import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="fancy-select"
export default class extends Controller {
  static targets = ["list", "item", "selected", "input", "search", "trigger"];
  static values = { searchUrl: String };

  connect() {
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener("click", this.boundHandleClickOutside);

    // Seed the hidden input from the server-rendered default once; from then
    // on the hidden input is the single source of truth for the selection.
    if (!this.inputTarget.value && this.element.dataset.defaultValue) {
      this.inputTarget.value = this.element.dataset.defaultValue;
    }
    this.applySelection();

    const form = this.element.closest("form");
    if (form) {
      form.addEventListener("submit", this._handleFormSubmit.bind(this));
    }

    // Set up debounced search for remote URLs
    this.searchDebounceTimer = null;
  }

  disconnect() {
    document.removeEventListener("click", this.boundHandleClickOutside);
  }

  toggle() {
    if (this.listTarget.classList.contains("hidden")) {
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    this.listTarget.classList.remove("hidden");
    if (this.hasSearchTarget) {
      this.searchTarget.focus();
    }
  }

  close() {
    this.listTarget.classList.add("hidden");
    if (this.hasSearchTarget) {
      this.searchTarget.value = "";
      this.filter();
    }
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close();
    }
  }

  // Reflect the hidden input's current value in the trigger label and the
  // checkmark of the matching list item (if it is present in the list).
  applySelection() {
    const id = this.inputTarget.value;
    if (!id) return;

    const selectItem = this.itemTargets.find((item) => item.dataset.id == id);
    if (!selectItem) return;

    this.selectedTarget.innerHTML =
      selectItem.querySelector(`[data-js="title"]`)?.outerHTML;
    selectItem.querySelector(`[data-js="check"]`).classList.remove("hidden");
  }

  choose(e) {
    const selectItem = e.currentTarget;
    const id = selectItem.dataset.id;

    // Copy the label HTML and place it into the outer form element]
    this.selectedTarget.innerHTML =
      selectItem.querySelector(`[data-js="title"]`).outerHTML;
    // Set the hidden inputs value to the ID
    this.inputTarget.value = id;
    // Remove checkmarks from all other elements
    this.itemTargets.forEach((itemTarget) => {
      itemTarget.querySelector(`[data-js="check"]`).classList.add("hidden");
    });
    // Add checkmark to selected item.
    selectItem.querySelector(`[data-js="title"]`);
    selectItem.querySelector(`[data-js="check"]`).classList.remove("hidden");

    this.close();

    // Trigger a change event on the input to propagate to any attached data actions
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));

    if (this.element.dataset.autoSubmit === "true") {
      // Find the closest form submit button and click it
      const submitButtons = this.element
        .closest("form")
        .querySelectorAll("button[type=submit]");
      if (submitButtons.length > 0) {
        submitButtons[submitButtons.length - 1].click();
      }
    }
  }

  filter() {
    const searchTerm = this.hasSearchTarget
      ? this.searchTarget.value.toLowerCase()
      : "";

    // If we have a remote search URL, use Turbo to fetch results
    if (this.hasSearchUrlValue && this.searchUrlValue) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.remoteSearch(searchTerm);
      }, 300); // Debounce for 300ms
    } else {
      // Client-side filtering for non-remote selects
      this.itemTargets.forEach((item) => {
        const searchText = item.dataset.searchText || "";
        const isVisible = searchText.includes(searchTerm);
        item.classList.toggle("hidden", !isVisible);
      });
    }
  }

  async remoteSearch(query) {
    const url = new URL(this.searchUrlValue, window.location.origin);
    url.searchParams.set("q", query);

    try {
      const response = await fetch(url);
      const html = await response.text();

      const matches = this.listTarget.querySelector("ul");
      matches.innerHTML = html;
      // Re-mark the current selection in the freshly loaded list. This must
      // not touch the hidden input, otherwise a pick made just before the
      // (debounced) reload would be reverted to the original value.
      this.applySelection();
    } catch (error) {
      console.error("Error fetching search results:", error);
    }
  }

  _handleFormSubmit(event) {
    const isRequired = this.inputTarget.required;
    const hasValue = this.inputTarget.value;

    const label = this.element.previousElementSibling;
    const trigger = this.triggerTarget;

    if (label && isRequired && !hasValue) {
      label.classList.add("text-destructive");
      trigger.classList.add("border-destructive");
      event.preventDefault();
    } else {
      label.classList.remove("text-destructive");
      trigger.classList.remove("border-destructive");
    }
  }
}
